import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { expensesAPI, favoritesAPI, mediaAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Receipt, 
  Trash2,
  Pencil,
  Calendar,
  DollarSign,
  Tag,
  Building2,
  Car,
  Bed,
  Package,
  Megaphone,
  Wrench,
  Shield,
  MoreHorizontal,
  FileText
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { HelpButton } from '../components/HelpButton';

const EXPENSE_CATEGORIES = [
  { value: 'booth_fee', label: 'Booth Fee', icon: Building2 },
  { value: 'travel', label: 'Travel (Gas, Tolls, Parking)', icon: Car },
  { value: 'lodging', label: 'Lodging', icon: Bed },
  { value: 'supplies', label: 'Supplies', icon: Package },
  { value: 'marketing', label: 'Marketing', icon: Megaphone },
  { value: 'equipment', label: 'Equipment', icon: Wrench },
  { value: 'insurance', label: 'Insurance', icon: Shield },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

export const Expenses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  const [formData, setFormData] = useState({
    event_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    category: 'other',
    description: '',
    amount: '',
    is_tax_deductible: true,
    receipt_media_id: '',
    notes: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expensesRes, eventsRes, mediaRes] = await Promise.all([
        expensesAPI.getAll(),
        favoritesAPI.getAll(),
        mediaAPI.getAll(),
      ]);
      setExpenses(expensesRes.data);
      setEvents(eventsRes.data);
      setMedia(mediaRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        event_id: expense.event_id || '',
        expense_date: expense.expense_date || new Date().toISOString().split('T')[0],
        category: expense.category || 'other',
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        is_tax_deductible: expense.is_tax_deductible !== false,
        receipt_media_id: expense.receipt_media_id || '',
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      setFormData({
        event_id: '',
        expense_date: new Date().toISOString().split('T')[0],
        category: 'other',
        description: '',
        amount: '',
        is_tax_deductible: true,
        receipt_media_id: '',
        notes: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount) {
      toast.error('Description and amount are required');
      return;
    }

    const payload = {
      event_id: formData.event_id || null,
      expense_date: formData.expense_date,
      category: formData.category,
      description: formData.description,
      amount: parseFloat(formData.amount),
      is_tax_deductible: formData.is_tax_deductible,
      receipt_media_id: formData.receipt_media_id || null,
      notes: formData.notes || null,
    };

    try {
      if (editingExpense) {
        await expensesAPI.update(editingExpense.id, payload);
        toast.success('Expense updated');
      } else {
        await expensesAPI.create(payload);
        toast.success('Expense added');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    
    try {
      await expensesAPI.delete(id);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const getCategoryIcon = (category) => {
    const found = EXPENSE_CATEGORIES.find(c => c.value === category);
    return found?.icon || MoreHorizontal;
  };

  const filteredExpenses = expenses.filter(exp => {
    const matchesEvent = eventFilter === 'all' || 
      (eventFilter === 'general' && !exp.event_id) ||
      exp.event_id === eventFilter;
    const matchesCategory = categoryFilter === 'all' || exp.category === categoryFilter;
    return matchesEvent && matchesCategory;
  });

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const taxDeductible = filteredExpenses.filter(e => e.is_tax_deductible).reduce((sum, e) => sum + e.amount, 0);
  
  // Expenses by category
  const byCategory = {};
  filteredExpenses.forEach(exp => {
    byCategory[exp.category] = (byCategory[exp.category] || 0) + exp.amount;
  });

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Expenses</h1>
              <p className="text-gray-600 mt-1">Track business and event expenses</p>
            </div>
            <HelpButton tutorialId="expenses" />
          </div>
          <Button 
            onClick={() => handleOpenDialog()} 
            className="btn-primary"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Expenses</p>
                <p className="text-xl font-bold text-red-600">${totalExpenses.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tax Deductible</p>
                <p className="text-xl font-bold text-green-600">${taxDeductible.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Expense Count</p>
                <p className="text-xl font-bold text-blue-600">{filteredExpenses.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        {Object.keys(byCategory).length > 0 && (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
            <h3 className="font-semibold text-[#1E3A5F] mb-3">By Category</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byCategory).map(([cat, amount]) => {
                const Icon = getCategoryIcon(cat);
                const catInfo = EXPENSE_CATEGORIES.find(c => c.value === cat);
                return (
                  <div key={cat} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <Icon className="w-4 h-4 text-[#1E3A5F]" />
                    <span className="text-sm">{catInfo?.label || cat}:</span>
                    <span className="font-mono font-semibold">${amount.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label>Filter by Event</Label>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger data-testid="event-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Expenses</SelectItem>
                  <SelectItem value="general">General (No Event)</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Filter by Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Expenses List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredExpenses.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No expenses found</h3>
            <p className="text-gray-600 mb-4">Start tracking your business expenses</p>
            <Button onClick={() => handleOpenDialog()} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard">
            <div className="divide-y divide-gray-100">
              {filteredExpenses.map((expense) => {
                const Icon = getCategoryIcon(expense.category);
                const catInfo = EXPENSE_CATEGORIES.find(c => c.value === expense.category);
                return (
                  <div
                    key={expense.id}
                    className="flex items-center gap-4 p-4 hover:bg-[#FFEDDF]/50 transition-colors"
                    data-testid={`expense-${expense.id}`}
                  >
                    <div className={`p-2 rounded-lg ${expense.is_tax_deductible ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${expense.is_tax_deductible ? 'text-green-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1E3A5F]">{expense.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(expense.expense_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {catInfo?.label || expense.category}
                        </span>
                        {expense.event?.title && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {expense.event.title}
                          </span>
                        )}
                        {expense.is_tax_deductible && (
                          <span className="text-green-600">Tax Deductible</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-red-600">${expense.amount.toFixed(2)}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(expense)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">
              {editingExpense ? 'Edit Expense' : 'Add Expense'}
            </DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update expense details' : 'Record a business expense'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    className="pl-8"
                    required
                    data-testid="expense-amount-input"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="What was this expense for?"
                required
                data-testid="expense-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event (Optional)</Label>
                <Select
                  value={formData.event_id || "none"}
                  onValueChange={(value) => setFormData({...formData, event_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="General expense" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">General (No Event)</SelectItem>
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Receipt (Optional)</Label>
              <Select
                value={formData.receipt_media_id || "none"}
                onValueChange={(value) => setFormData({...formData, receipt_media_id: value === "none" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Link to media library" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No receipt</SelectItem>
                  {media.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="is_tax_deductible"
                checked={formData.is_tax_deductible}
                onCheckedChange={(checked) => setFormData({...formData, is_tax_deductible: checked})}
              />
              <Label htmlFor="is_tax_deductible" className="cursor-pointer">
                Tax Deductible
              </Label>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-expense-btn">
                {editingExpense ? 'Update' : 'Add'} Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
